import { createLogger } from "./logger";

const logger = createLogger("dismissalStore");

const STORAGE_KEY = "pm:dismissals";
const VERSION = 1;

type DismissalRecord = Record<string, number>;
type DismissalStore =
  | {
      version: number;
      users: Record<string, DismissalRecord>;
    }
  | DismissalRecord; // legacy shape

const now = () => Date.now();

const getStorage = () => {
  if (typeof window === "undefined") {
    return null;
  }
  try {
    return window.localStorage ?? window.sessionStorage;
  } catch (err) {
    logger.warn("Failed to access primary dismissal storage", err);
    return null;
  }
};

const getFallbackStorage = () => {
  if (typeof window === "undefined") {
    return null;
  }
  try {
    return window.sessionStorage;
  } catch {
    return null;
  }
};

const parseStore = (raw: string | null): DismissalStore => {
  if (!raw) {
    return { version: VERSION, users: {} };
  }
  try {
    const parsed = JSON.parse(raw);
    if (parsed && typeof parsed === "object" && "version" in parsed && "users" in parsed) {
      return parsed as DismissalStore;
    }
    if (parsed && typeof parsed === "object") {
      return parsed as DismissalRecord;
    }
  } catch (err) {
    logger.warn("Failed to parse dismissal store", err);
  }
  return { version: VERSION, users: {} };
};

const readFromStorage = (storage: Storage | null): DismissalStore => {
  if (!storage) {
    return { version: VERSION, users: {} };
  }
  return parseStore(storage.getItem(STORAGE_KEY));
};

const mergeStores = (
  primary: DismissalStore,
  fallback: DismissalStore,
): { version: number; users: Record<string, DismissalRecord> } => {
  // Prefer versioned store; merge legacy as anon scope to avoid cross-user bleed.
  const base: { version: number; users: Record<string, DismissalRecord> } = {
    version: VERSION,
    users: {},
  };

  const apply = (store: DismissalStore) => {
    if ("version" in store && "users" in store) {
      Object.entries(store.users ?? {}).forEach(([scope, dismissals]) => {
        base.users[scope] = { ...(base.users[scope] ?? {}), ...(dismissals ?? {}) };
      });
      return;
    }
    // legacy shape: treat as anonymous scope only
    base.users.anon = { ...(base.users.anon ?? {}), ...(store as DismissalRecord) };
  };

  apply(fallback);
  apply(primary);
  return base;
};

const readStore = (): { version: number; users: Record<string, DismissalRecord> } => {
  const primary = readFromStorage(getStorage());
  const fallback = readFromStorage(getFallbackStorage());
  return mergeStores(primary, fallback);
};

const writeStore = (store: { version: number; users: Record<string, DismissalRecord> }) => {
  const storage = getStorage() ?? getFallbackStorage();
  if (!storage) {
    return;
  }
  try {
    storage.setItem(STORAGE_KEY, JSON.stringify(store));
  } catch (err) {
    logger.warn("Failed to persist dismissal store", err);
  }
};

const scopeKey = (userId?: string | null) => (userId ? `user:${userId}` : "anon");

export const isDismissed = (id: string, userId?: string | null): boolean => {
  const store = readStore();
  const scoped = store.users[scopeKey(userId)] ?? {};
  const expiry = scoped[id];
  if (typeof expiry !== "number") {
    return false;
  }
  if (expiry <= now()) {
    const nextScoped = { ...scoped };
    delete nextScoped[id];
    const nextStore = {
      ...store,
      users: {
        ...store.users,
        [scopeKey(userId)]: nextScoped,
      },
    };
    writeStore(nextStore);
    return false;
  }
  return true;
};

export const dismissUntil = (id: string, expiresAtMs: number, userId?: string | null) => {
  const store = readStore();
  const scoped = store.users[scopeKey(userId)] ?? {};
  const nextStore = {
    version: VERSION,
    users: {
      ...store.users,
      [scopeKey(userId)]: {
        ...scoped,
        [id]: expiresAtMs,
      },
    },
  };
  writeStore(nextStore);
};
